import classNames from 'classnames';
import { FunctionComponent, useEffect, useState } from 'react';
import styles from './AppTemplate.module.scss';
import { Outlet, useNavigate } from 'react-router-dom';
import MainFooter from '../../../../../components/footers/MainFooter/MainFooter';
import { getCurrentUserRole } from '../../../../../utils/role';
import AppHeader from '../../../components/headers/AppHeader/AppHeader';
import FooterSection from '../../../components/sections/FooterSection/FooterSection';
import SectionDivider from '../../../../../components/sections/SectionDivider/SectionDivider';
import MainButton from '../../../../../components/buttons/MainButton/MainButton';
import RoutesEnum from '../../../../../enums/routes';
import DevToolLinks from '../../../../../components/devTools/DevToolLinks/DevToolLinks';
import { RoutesGrouped } from '../../../../../data/routes';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { closeModal, openModal, selectIsModalOpen } from '../../../../../redux/slices/modal/slice';
import ContactSupportPopUpKarla from '../../../components/popups/ContactSupportPopUpKarla/ContactSupportPopUpKarla';
import { CLIENT_SUPPORT_CONTENT } from '../../../data/clientSupport';
import AppsSelectorPopUp from '../../../../../components/popups/AppsSelectorPopUp/AppsSelectorPopUp';
import api from '../../../../../api';
import { AxiosError } from 'axios';
import { ApiResponseDTO } from '../../../../../dto/api';
import { clearAttendeeId, setAttendeeId } from '../../../../../utils/webStorage/attendeeIdStorage';

interface MainTemplateProps extends React.PropsWithChildren {
  hideFooterSection?: boolean;
}

const AppTemplate: FunctionComponent<MainTemplateProps> = ({ children, hideFooterSection = false }) => {
  const navigate = useNavigate();
  const userRole = getCurrentUserRole();

  const dispatch = useAppDispatch();

  const isLoginModalOpen = useAppSelector((state) => selectIsModalOpen(state, CLIENT_SUPPORT_CONTENT.modalId));
  const userId = useAppSelector((state) => state.user.user?.userId);

  // Handlers for the modal state
  const handleLoginModalStateChange = (state: boolean) => {
    if (!state) {
      dispatch(closeModal(CLIENT_SUPPORT_CONTENT.modalId));
    }
  };

  // Get attendee id
  useEffect(() => {
    const getAttendeeId = async () => {
      try {
        const response = await api.ConstellationDinners.getAttendeeID(userId!);
        // const response = await api.ConstellationDinners.getAttendeeID('2C68A2F8-DED7-4C1E-246B-08DD87D3BAF1');
        // const response = await api.ConstellationDinners.getAttendeeID('2C68A2F8-DED7-4C1E-246B-08DD87D3BAF2');

        const responseData = response.data;
        if (responseData.success === true && responseData.data) {
          setAttendeeId(responseData.data);
        } else {
          console.error('Error:', responseData.errors);
          clearAttendeeId();
        }
      } catch (error) {
        const errorObj = error as AxiosError<ApiResponseDTO>;
        console.error('Error: ', errorObj);
        clearAttendeeId();
      }
    };

    getAttendeeId();
  }, []);
  // Get attendee id end

  return (
    <div className={classNames(styles['main-template'], 'template main-template')}>
      <AppHeader />

      <main style={{ overflow: 'hidden' }}>
        <Outlet />

        <SectionDivider color="blue" />

        <FooterSection
          title="Supernova Dinners"
          subTitle="A collision of value"
          text="Get a curated list of 20-30 accredited investors for your intimate living room dinner, with personalized coaching, investor approvals, and follow-up support to ensure your dinner fosters meaningful connections and drives interest in your raise."
          buttonGroup={[
            <MainButton
              onClick={() => navigate(`/${userRole}/${RoutesEnum.CONNECT}/${RoutesEnum.FUNDRAISING_DINNERS}`)}
            >
              Learn more
            </MainButton>,
          ]}
        />
      </main>

      <MainFooter userRole={userRole} />

      <DevToolLinks
        title="Sitemap"
        position="bottom-left"
        links={[
          {
            label: 'Dinning Preferences',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.DINING_PREFERENCES}`,
          },
          {
            label: 'Code of Conduct',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.CODE_OF_CONDUCT}`,
          },
          {
            label: 'Upcoming Event',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.UPCOMING_EVENT}`,
          },
          {
            label: 'Past Events',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.PAST_EVENTS}`,
          },
          {
            label: 'Past Events (detailed)',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.PAST_EVENTS}/past-event-test`,
          },
          {
            label: 'Preferences (general)',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.PREFERENCES}/${RoutesEnum.GENERAL}`,
          },
          {
            label: 'Preferences (dinning preferences)',
            url: `/${RoutesGrouped.CONSTELLATION_DINNERS_BASE}/${RoutesEnum.PREFERENCES}/${RoutesEnum.DINING_PREFERENCES}`,
          },
        ]}
      />

      <ContactSupportPopUpKarla active={isLoginModalOpen} setActive={handleLoginModalStateChange} />
    </div>
  );
};

export default AppTemplate;
